import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import classes from './MediaGridItem.module.scss';
import { Loader } from '../../../../components/containers/Loader';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { IconsEnum } from '../../../../constants/icons';
import { MediaItem, MediaItemSelectedType } from '../../../../models/media';
import { MediaType } from '../../../../models/graphql';
import { Icon } from 'components/images/Icon/Icon';
import { ButtonMenu } from '../../../../components/actions/ButtonMenu';
import { ContainerListItem } from '../../../../components/actions/ListItem/ListItem.view';
import { CheckboxWithAction } from '../../../../components/actions/CheckboxWithAction';
import { MediaGridContext } from '../MediaGrid/MediaGrid.view';
import VideoPlayButton from '../../../../assets/images/VideoPlayButton.svg';
import { ReactComponent as NoVideoImage } from 'assets/images/VideoProcessing.svg';
import { ReactComponent as NoImage } from 'assets/images/linkPreviewNoImage.svg';
import classNames from 'classnames';
import { Debugger } from '../../../../components/containers/Debugger';
import { RedExclamationTooltip } from '../../../../components/actions/RedExclamationTooltip';
import { Badge } from '../../../../components/containers/Badge';
import { BadgeColorsEnum, BadgeSizesEnum } from '../../../../constants/badge';
import { ElementOverlay } from '../../../../components/containers/ElementOverlay';
import {
  ElementOverlayCursor,
  ElementOverlayPositions,
  ElementOverlaySize
} from '../../../../components/containers/ElementOverlay/ElementOverlay.view';

export type MediaGridItemViewProps = {
  context: MediaGridContext;
  mediaItem: MediaItem;
  setShowLightboxModal: (show: boolean) => void;
  setSelectedMediaItemForLightbox: (item: MediaItem) => void;
  selectedMediaItems: MediaItemSelectedType[];
  menuListItems: ContainerListItem[];
  checkboxHandler: (mediaItem: MediaItemSelectedType, e: React.MouseEvent<HTMLInputElement>) => void;
  shareHandler?: (media: MediaItem[]) => void;
  setImagesIdsWithErrorSelected: Dispatch<SetStateAction<MediaItemSelectedType[]>>;
};

export const MediaGridItemView = (props: MediaGridItemViewProps): JSX.Element => {
  const { shareHandler, context, mediaItem, menuListItems, checkboxHandler, setImagesIdsWithErrorSelected } = props;
  const isChecked = props.selectedMediaItems.some(item => item.id === mediaItem.id && item.type === mediaItem.type);
  const isTranscoding = mediaItem.type === MediaType.VIDEO && !mediaItem.transcoded;
  const [stickyMenu, setStickyMenu] = useState<boolean>(false);
  const [loaded, setLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const mediaContainerClasses = classNames(classes.MediaContainer, {
    [classes.MediaContainerOpacity50]: isTranscoding
  });

  const rightTopIconElement = useMemo(() => {
    if (mediaItem.type === MediaType.VIDEO) {
      return (
        <div className={'grid-x'}>
          {isTranscoding && (
            <div className={classNames('cell auto', classes.TranscodingTooltipWrapper)}>
              <RedExclamationTooltip
                id={mediaItem.id}
                description={
                  'We are currently processing this video. It will be available for playback once the processing is complete.'
                }
                size={'large'}
              />
            </div>
          )}
          <div className={'cell auto'}>
            <div className={classes.OverlayElementRightTop}>
              <Icon name={IconsEnum.video} size={'large'} />
            </div>
          </div>
        </div>
      );
    } else {
      return <></>;
    }
  }, [isTranscoding, mediaItem]);

  const stickyPositions = [
    ElementOverlayPositions.MiddleCenter,
    ...(isChecked ? [ElementOverlayPositions.TopLeft] : []),
    ...(rightTopIconElement ? [ElementOverlayPositions.TopRight] : []),
    ...(stickyMenu ? [ElementOverlayPositions.BottomRight] : [])
  ];

  const leftTopElement = useMemo(() => {
    return (
      <div className={classes.OverlayElementLeftTop}>
        <CheckboxWithAction
          name={'media'}
          size={'xlarge'}
          onClickHandler={e => {
            if (imageError && !isChecked) {
              setImagesIdsWithErrorSelected(state => [...state, ...[{ id: mediaItem.id, type: mediaItem.type }]]);
            } else {
              setImagesIdsWithErrorSelected(state => {
                return state.filter(media => !(media.id === mediaItem.id && media.type === mediaItem.type));
              });
            }

            checkboxHandler({ id: mediaItem.id, type: mediaItem.type }, e);
          }}
          value={mediaItem.id}
          checked={isChecked}
        />
      </div>
    );
  }, [mediaItem, isChecked, imageError, checkboxHandler, setImagesIdsWithErrorSelected]);

  const leftBottomElement = useMemo(() => {
    switch (context) {
      case MediaGridContext.POST_EDITOR_MEDIA_LIBRARY:
      case MediaGridContext.POST_EDITOR_FIND_IMAGES:
        return (
          <div className={classes.leftBottomElementClasses}>
            <Button
              type={'button'}
              variant={'solid'}
              size={'small'}
              color={ButtonColorsEnum.white}
              action={() => shareHandler && shareHandler([mediaItem])}>
              Add to Post
            </Button>
          </div>
        );
      default:
        return (
          <div className={classes.leftBottomElementClasses}>
            <Button
              type={'button'}
              variant={'solid'}
              size={'small'}
              color={ButtonColorsEnum.white}
              icon={{ side: 'left', value: IconsEnum.share }}
              action={() => shareHandler && shareHandler([mediaItem])}>
              Share
            </Button>
          </div>
        );
    }
  }, [context, shareHandler, mediaItem]);

  // Function to modify the action method of a ContainerListItem to inject the close of the menu when clicked
  const modifyAction = (item: ContainerListItem): ContainerListItem => {
    const originalAction = item.action;
    return {
      ...item,
      action: (...args) => {
        setStickyMenu(false);
        originalAction(...args);
      }
    };
  };

  const rightBottomElement = useMemo(() => {
    const listItem = isTranscoding
      ? [menuListItems.find(i => i.label === 'Delete')]
          .filter((item): item is ContainerListItem => Boolean(item))
          .map(modifyAction)
      : menuListItems.map(modifyAction);
    const listItemNotLoaded = listItem.filter(item => item.label === 'Delete');

    switch (context) {
      case MediaGridContext.POST_EDITOR_MEDIA_LIBRARY:
      case MediaGridContext.POST_EDITOR_FIND_IMAGES:
        return <></>;
      default:
        return (
          <div className={classes.rightBottomElementClasses}>
            <ButtonMenu
              isSquareButton={false}
              variant={'solid'}
              size={'small'}
              icon={IconsEnum.threeDot}
              buttonType={'transparent'}
              side={'right'}
              listItems={loaded ? listItem : listItemNotLoaded}
              closeOnMouseOut={false}
              onMenuShowEventCallback={setStickyMenu}
            />
          </div>
        );
    }
  }, [context, isTranscoding, menuListItems, loaded]);

  const middleCenter =
    mediaItem.type === 'VIDEO' ? (
      <div className={classes.MiddleCenterIconWrapper}>
        {mediaItem.transcoded ? (
          <img src={VideoPlayButton} className={classes.PlayIcon} alt={'Video play button'} />
        ) : (
          <NoVideoImage />
        )}
      </div>
    ) : imageError ? (
      <NoImage />
    ) : undefined;

  const MediaImage = useMemo((): JSX.Element => {
    const { thumbnailUrl, url, type } = mediaItem;
    const imgClasses = classNames(loaded ? classes.Loaded : classes.NotLoaded);
    const thumbImage = thumbnailUrl?.startsWith('data:image') || !thumbnailUrl ? url : thumbnailUrl;
    const imageElement = (
      <img
        className={imgClasses}
        src={thumbImage}
        alt={`${mediaItem.fileName}-${mediaItem.id}`}
        onLoad={() => setLoaded(true)}
        onError={() => setImageError(true)}
      />
    );

    // If the image comes back with an error, we show the placeholder image
    // This is to facilitate a different image/message
    if (imageError) {
      return <div className={classes.PlaceHolderImageWrapper} />;
    }

    if (type === MediaType.VIDEO) {
      return <div className={mediaContainerClasses}>{imageElement}</div>;
    }

    if (type === MediaType.PHOTO && (thumbnailUrl || url)) {
      return <div className={classes.MediaContainer}>{imageElement}</div>;
    }

    // default to the placeholder
    return <div className={classes.PlaceHolderImageWrapper} />;
  }, [loaded, imageError, mediaContainerClasses, mediaItem]);

  return (
    <div className={classes.MediaGridItem}>
      <>
        {props.mediaItem.isUploading ? (
          <div style={{ position: 'relative' }}>
            <Debugger tooltipContent={`Media ID: ${mediaItem.id}`} modalContent={mediaItem} />
            {MediaImage}
            <div className={classes.Loader}>
              <Loader variation={'dark'} size={'medium'} type={'inline'} />
            </div>
          </div>
        ) : (
          <ElementOverlay
            size={ElementOverlaySize.MEDIUM}
            stickyPositions={stickyPositions}
            defaultAction={
              loaded &&
              (props.mediaItem.type === MediaType.PHOTO ||
                (props.mediaItem.type === MediaType.VIDEO && props.mediaItem.transcoded))
                ? () => {
                    props.setSelectedMediaItemForLightbox(props.mediaItem);
                    props.setShowLightboxModal(true);
                  }
                : undefined
            }
            topLeft={leftTopElement}
            topCenter={
              mediaItem.createdBy ? (
                <Badge color={BadgeColorsEnum.DARK} size={BadgeSizesEnum.MEDIUM} text={mediaItem.createdBy} />
              ) : undefined
            }
            bottomLeft={loaded ? leftBottomElement : <></>}
            topRight={rightTopIconElement}
            bottomRight={rightBottomElement}
            middleCenter={middleCenter}
            cursor={ElementOverlayCursor.magnify}>
            <Debugger tooltipContent={`Media ID: ${mediaItem.id}`} modalContent={mediaItem} />
            {MediaImage}
          </ElementOverlay>
        )}
      </>
    </div>
  );
};
